import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import {
  UserService,
  LoaderService,
  SentryService,
  LegalDocumentsService,
  GDPRService,
  SEOService
} from '@box-core/services';
import { finalize, catchError } from 'rxjs/operators';
import { deleteJsonLd } from '@box/utils';
import { environment } from '@box-env/environment';
import { APIError, SeoOptions } from '@box-types';
import { of } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalStateService } from '@box-core/services/global-state.service';

type ConsentOption = { text: string; value: boolean };

@Component({
  selector: 'account-personalisation-terms',
  templateUrl: './account-personalisation-terms.page.html',
  styleUrls: ['./account-personalisation-terms.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class AccountPersonalisationTermsPage implements OnInit, OnDestroy {
  public termsToggled: boolean;
  public termsLoading: boolean;
  public termsStylesUrl: string;
  public marketingTermsHtml: string;
  public readonly consentOptions: ConsentOption[] = [
    { text: 'account_personalisation_terms_option_1', value: true },
    { text: 'account_personalisation_terms_option_2', value: false }
  ];
  public submittedConsentValue: boolean;
  public selectedConsentValue: boolean;
  public canSubmit: boolean;

  constructor(
    private gdprService: GDPRService,
    private loaderService: LoaderService,
    private legalDocumentsService: LegalDocumentsService,
    private sentryService: SentryService,
    private changeDetectorRef: ChangeDetectorRef,
    private activatedRoute: ActivatedRoute,
    private seoService: SEOService,
    private router: Router,
    private globalStateService: GlobalStateService
  ) {}

  ngOnInit(): void {
    const gdprConsent = this.globalStateService.getUser().gdprConsent;
    this.submittedConsentValue = gdprConsent?.accepted;
    this.selectedConsentValue = this.submittedConsentValue;
    this.canSubmit = false;
    this.termsStylesUrl = environment.application.legalDocuments.STYLES_URL;
    this.setMetaTags();
  }

  ngOnDestroy(): void {
    deleteJsonLd();
  }

  public onConsentOptionChange(consentValue: boolean): void {
    this.selectedConsentValue = consentValue;
    this.canSubmit = this.submittedConsentValue !== this.selectedConsentValue;
    this.changeDetectorRef.detectChanges();
  }

  public submitConsentOption(): void {
    if (!this.canSubmit) return;
    this.loaderService.setState(true);
    this.gdprService
      .updateGDPRConsent(this.selectedConsentValue)
      .pipe(
        finalize(() => this.loaderService.setState(false)),
        catchError((error: APIError) => {
          this.sentryService.captureException(error, {
            domain: 'Account Terms',
            domainDetails: 'Set GDPR Consent',
            severity: 'warning'
          });
          return of(this.globalStateService.getUser().gdprConsent);
        })
      )
      .subscribe({
        next: (gdprConsent) => {
          this.submittedConsentValue = gdprConsent.accepted;
          this.selectedConsentValue = gdprConsent.accepted;
          this.canSubmit = false;
          this.globalStateService.setUser({ ...this.globalStateService.getUser(), gdprConsent });
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  public toggleTermsDocument(): void {
    this.termsToggled = !this.termsToggled;
    this.changeDetectorRef.detectChanges();
    if (this.termsToggled) this.fetchTerms();
  }

  private fetchTerms(): void {
    this.termsLoading = true;
    this.changeDetectorRef.detectChanges();
    this.legalDocumentsService
      .fetchMarketingTerms()
      .pipe(
        finalize(() => {
          this.termsLoading = false;
          this.changeDetectorRef.detectChanges();
        })
      )
      .subscribe({
        next: (terms) => (this.marketingTermsHtml = terms),
        error: (error: APIError) =>
          this.sentryService.captureException(error, {
            domain: 'Account Terms',
            domainDetails: 'Fetch Marketing Terms Document',
            severity: 'warning'
          })
      });
  }

  private setMetaTags(): void {
    const options: SeoOptions = { title: this.activatedRoute.snapshot.data.breadcrumbName, url: this.router.url };
    this.seoService.setTags(options);
  }
}
